/*
 * Label history, design review option (d), which Dawid agreed: the status in the head with who set
 * it, its change log one click away, and a quiet read-only intent on each lead message. Two things
 * kept apart: a message's intent is what that one email said and never changes; the status belongs
 * to the conversation, changes, and has an owner. Only the status is edited (the head's label menu).
 * No label event sits between emails any more, so the reading path is the lead's own words; the
 * stacked dividers above the newest reply read as a story that never happened.
 *
 * Built on the thread's `changes` (every decision, confirmations included, from `?history=1`), its
 * `meetingChanges` (a rep's word on the meeting flag, LEA-494) and each message's `intent`. Until the
 * backfill of earlier messages runs, only the newest lead message carries an intent.
 */
import { type ReactNode, useId } from "react";
import { appDateTime, appDayMonth } from "../../src/app-dates.ts";
import type { InboxLabelCode } from "../../src/inbox-label.ts";
import type {
  InboxLabelChange,
  InboxMeetingChange,
  InboxMessage,
  InboxThread,
} from "../../src/inbox-v2/contract.ts";
import { LabelDot, LabelPill } from "./label-key.tsx";
import { LABEL_NAMES } from "./labels.ts";
import { MeetingRequestMarker } from "./meeting-marker.tsx";

/**
 * Who set the status now on the conversation, and when, as the head's link to its history says it:
 * "Set by Mara" or "Labelled by the AI" ("Mara" or "AI" where the line is narrow), and "14 Oct".
 */
export function statusSetBy(change: InboxLabelChange): {
  who: string;
  /** The same, cut for a narrow line: "By Mara", "By the AI", or "No label" when there is none. */
  short: string;
  when: string;
} {
  const rep = change.who ?? "a rep";
  const bare = change.to === null;
  const byRep = bare ? `Label cleared by ${rep}` : `Set by ${rep}`;
  return {
    who: change.by === "rep" ? byRep : bare ? "No label from the AI" : "Labelled by the AI",
    short: bare ? "No label" : change.by === "rep" ? `By ${rep}` : "By the AI",
    when: appDayMonth(change.at),
  };
}

/** What the head's history link says: who set the status, cut short for a narrow line, and when. */
type StatusWords = ReturnType<typeof statusSetBy>;

/** The rep's latest word on the newest lead message's meeting flag; null where nobody gave one. */
function meetingMarkOf(thread: InboxThread): InboxMeetingChange | null {
  const newest = thread.conversation.leadMessageId;
  return (thread.meetingChanges ?? []).filter((mark) => mark.messageId === newest).at(-1) ?? null;
}

/** Where a meeting request came from, for its marker's tip: who marked it, or the AI's reading. */
export function meetingNote(thread: InboxThread): string {
  const mark = meetingMarkOf(thread);
  return mark?.meeting
    ? `Marked as a meeting request by ${mark.who ?? "a rep"}, ${appDayMonth(mark.at)}`
    : "The AI read a meeting ask in the newest message";
}

/**
 * The head's status line (LEA-494 adds the meeting flag): the newest of the label's last decision
 * and a rep's word on the flag says who set what, and wins a tie, as a yes that also relabelled is
 * one gesture. Beside the Meeting request marker a rep's yes is "Marked by Mara"; a no, with no
 * marker to stand beside, is "Meeting request cleared by Mara". A meeting request the AI flagged
 * names its signal on the AI's label line: "Labelled by the AI: meeting ask". Short, as the line
 * does not wrap on a desktop and the history link is its last item.
 */
export function headStatus(thread: InboxThread): StatusWords | null {
  const change = thread.changes.at(-1);
  const mark = meetingMarkOf(thread);
  if (mark && (!change || Date.parse(mark.at) >= Date.parse(change.at))) {
    const rep = mark.who ?? "a rep";
    return {
      who: mark.meeting ? `Marked by ${rep}` : `Meeting request cleared by ${rep}`,
      short: `By ${rep}`,
      when: appDayMonth(mark.at),
    };
  }
  if (!change) return null;
  const words = statusSetBy(change);
  const flaggedByAi = thread.conversation.meetingRequest === true && mark === null;
  return flaggedByAi && change.by === "ai" ? { ...words, who: `${words.who}: meeting ask` } : words;
}

/** A label in the log, or "No label" as a word where a change took one away. */
function LogLabel(props: { code: InboxLabelCode | null }) {
  if (props.code === null) return <span className="lcr-i2-log-none">No label</span>;
  return <LabelPill code={props.code} />;
}

/** Who made a decision, and how: a person's change or confirmation, or the AI on which reply. */
function decidedBy(change: InboxLabelChange, newest: string | null): string {
  if (change.by === "rep")
    return `${change.who ?? "A rep"} · ${change.confirmed ? "confirmed" : "manual"}`;
  return `AI · ${change.messageId === newest ? "newest reply" : "earlier reply"}`;
}

/** One decision: when, from what to what (or what it confirmed), who and how. */
function LogEntry(props: { change: InboxLabelChange; newest: string | null }) {
  const { change } = props;
  const moved = change.by === "rep" && !change.confirmed && change.from !== change.to;
  return (
    <li>
      <time dateTime={change.at}>{appDateTime(change.at)}</time>
      <span className="lcr-i2-log-labels">
        {moved && (
          <>
            <LogLabel code={change.from} />
            <span className="lcr-i2-log-to">to</span>
          </>
        )}
        <LogLabel code={change.to} />
      </span>
      <span className="lcr-i2-log-who">{decidedBy(change, props.newest)}</span>
    </li>
  );
}

/**
 * A rep's word on the meeting flag, with the relabel to Interested a yes made in the same gesture:
 * one entry, "Marked as a meeting request; label set to Interested" (the design's decision 5).
 */
function MarkEntry(props: { mark: InboxMeetingChange; relabel: InboxLabelChange | null }) {
  const { mark, relabel } = props;
  const who = mark.who ?? "A rep";
  return (
    <li>
      <time dateTime={mark.at}>{appDateTime(mark.at)}</time>
      <span className="lcr-i2-log-labels">
        {relabel && (
          <>
            <LogLabel code={relabel.from} />
            <span className="lcr-i2-log-to">to</span>
            <LogLabel code={relabel.to} />
          </>
        )}
        {mark.meeting ? (
          <MeetingRequestMarker note="Marked as a meeting request" />
        ) : (
          <span className="lcr-i2-log-none">Not a meeting request</span>
        )}
      </span>
      <span className="lcr-i2-log-who">
        {who} · {mark.meeting ? "marked" : "cleared"}
        {relabel ? ", label set to Interested" : ""}
      </span>
    </li>
  );
}

type LogItem =
  | { readonly kind: "label"; readonly change: InboxLabelChange }
  | {
      readonly kind: "meeting";
      readonly mark: InboxMeetingChange;
      readonly relabel: InboxLabelChange | null;
    };

/** The relabel a yes wrote with its mark: the same person, the same moment, to Interested. */
const madeWith = (mark: InboxMeetingChange, change: InboxLabelChange): boolean =>
  mark.meeting &&
  change.by === "rep" &&
  !change.confirmed &&
  change.to === "interested" &&
  change.actorId === mark.actorId &&
  change.messageId === mark.messageId &&
  Date.parse(change.at) === Date.parse(mark.at);

/** Every decision and mark, oldest first; a yes and the relabel it made are one item. */
function logItems(thread: InboxThread): LogItem[] {
  const marks = thread.meetingChanges ?? [];
  const absorbed = new Set<InboxLabelChange>();
  const items: { at: number; item: LogItem }[] = marks.map((mark) => {
    const relabel = thread.changes.find(
      (change) => !absorbed.has(change) && madeWith(mark, change),
    );
    if (relabel) absorbed.add(relabel);
    return { at: Date.parse(mark.at), item: { kind: "meeting", mark, relabel: relabel ?? null } };
  });
  for (const change of thread.changes)
    if (!absorbed.has(change))
      items.push({ at: Date.parse(change.at), item: { kind: "label", change } });
  // Stable: at the same moment a label decision reads before the mark that followed it.
  return items
    .map((entry, index) => ({ ...entry, order: entry.item.kind === "label" ? 0 : 1, index }))
    .sort((a, b) => a.at - b.at || a.order - b.order || a.index - b.index)
    .map((entry) => entry.item);
}

/**
 * The change log, newest first, in a panel under the head: every decision on this conversation and
 * every word on its meeting flag, then what else the head keeps out of its one meta line
 * (`children`: who assigned it, who has read it).
 */
export function ChangeLog(props: { thread: InboxThread; id: string; children?: ReactNode }) {
  const newest = props.thread.conversation.leadMessageId;
  const titleId = useId();
  const items = logItems(props.thread);
  return (
    <section className="lcr-i2-change-log" id={props.id} aria-labelledby={titleId}>
      <h3 id={titleId}>
        {items.length > 0 ? "Change log" : "History"}{" "}
        <span className="lcr-i2-change-log-scope">· this conversation</span>
      </h3>
      {items.length > 0 && (
        <ol>
          {items
            .reverse()
            .map((item) =>
              item.kind === "label" ? (
                <LogEntry
                  key={item.change.id ?? item.change.at}
                  change={item.change}
                  newest={newest}
                />
              ) : (
                <MarkEntry key={item.mark.id} mark={item.mark} relabel={item.relabel} />
              ),
            )}
        </ol>
      )}
      {props.children}
    </section>
  );
}

/** What the AI read in one lead message. */
export type AiReading = { code: InboxLabelCode; uncertain: boolean };

/**
 * What the AI read in a lead message: its stored intent, or, once a person has relabelled the
 * conversation, the AI's own last decision on that message, since the store then keeps the person's
 * label on the newest message. Ours never carry one.
 */
export function aiReading(
  message: InboxMessage,
  changes: readonly InboxLabelChange[],
): AiReading | null {
  if (message.direction !== "inbound") return null;
  const intent = message.intent;
  if (intent?.by === "ai" && intent.code !== null)
    return { code: intent.code, uncertain: intent.uncertain };
  const decided = changes
    .filter((change) => change.by === "ai" && change.messageId === message.id)
    .at(-1);
  return decided?.to ? { code: decided.to, uncertain: false } : null;
}

/** What a lead message said, as the AI read it: quiet, read-only, never the status. */
export function IntentTag(props: { reading: AiReading | null }) {
  const { reading } = props;
  if (!reading) return null;
  return (
    <span
      className="lcr-i2-intent"
      title="What this email said, as the AI read it. Read-only: the status is set in the header."
    >
      <LabelDot code={reading.code} unsure={reading.uncertain} />
      <span className="lcr-visually-hidden">This email: </span>
      {LABEL_NAMES[reading.code]}
      {reading.uncertain && <span className="lcr-visually-hidden"> (the AI was not sure)</span>}
    </span>
  );
}
